import React, { FC, useCallback, useEffect, useState } from 'react';
import { get } from 'utils/rest-clients';
import { useMutation } from '@apollo/client';
import { AxiosError } from 'utils/gql/axios/AxiosClient';
import { TenantFeatureModel } from '@utmost.co/enterprise-models';
import { useDeepMemo } from '@utmost.co/utmost-design-system/src/hooks/useCompare';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { FeaturesNames } from 'src/definitions/custom-models';
import { CustomTenantModel, TenantContextProps } from './Tenant.types';
import { UPDATE_FEATURES } from './Tenant.mutations';
import { TenantContext } from './TenantContext';
import { BpeEnvironmentType, bpeConfig } from '@enterprise/common';

interface Props {
  tenant: CustomTenantModel | undefined;
}

// NOTE: Feature flags will not work in this component.
export const TenantProvider: FC<React.PropsWithChildren<Props>> = ({ children, tenant: initialTenant }) => {
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useSnackbar();
  const [tenant, setTenant] = useState<CustomTenantModel | undefined>(initialTenant);
  const [error, setError] = useState<AxiosError | undefined>();
  const [updateFeature] = useMutation<
    { updateFeature: TenantFeatureModel },
    { key: FeaturesNames; feature: Partial<TenantFeatureModel> }
  >(UPDATE_FEATURES);

  const updateFeatureHandler = useCallback(
    async (key: FeaturesNames, feature: Partial<TenantFeatureModel>, showGQLError?: boolean) => {
      await updateFeature({
        variables: { key, feature },
        onCompleted: (data) => {
          const updated = data?.updateFeature;
          if (updated) {
            setTenant((prev) => (prev ? { ...prev, features: { ...prev.features, [key]: updated } } : prev));
          }
          showSuccess('Feature was updated successfully');
        },
        onError: (error) => showError(showGQLError ? error.message : 'Feature cannot be updated')
      });
    },
    [updateFeature, showSuccess, showError]
  );

  const getTenant = useCallback(
    async (tenantCodeParam?: string) => {
      setLoading(true);
      try {
        const response = await get<CustomTenantModel>(`/tenants/${tenantCodeParam ?? tenant?.code}`);
        setTenant(response.data);
      } catch (e) {
        const axiosError = e as AxiosError;
        setError(axiosError);
      }
      setLoading(false);
    },
    [tenant?.code]
  );

  useEffect(() => {
    if (!!tenant?.tenantConfig?.environment && bpeConfig.environmentType !== tenant?.tenantConfig?.environment) {
      bpeConfig.environmentType = tenant?.tenantConfig?.environment as Uppercase<BpeEnvironmentType>;
    }
  }, [tenant]);

  const value: TenantContextProps = useDeepMemo(
    (): TenantContextProps => ({
      loading,
      tenantCode: tenant?.code,
      tenant,
      error,
      actions: { getTenant, setTenant, updateFeature: updateFeatureHandler }
    }),
    [loading, tenant, error, getTenant, updateFeatureHandler]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
